import {
  computePhotoFrame,
  PHOTO_ASPECTS,
  photoAspectLabel,
  type PhotoAspect,
} from '@viewer/overlay/photoTypes';
import { PHOTO_LENSES, photoLensLabel, type PhotoLens } from '@viewer/overlay/photoLens';

export type { PhotoAspect, PhotoLens };

export class PhotoOverlay {
  private active = false;
  private chromeVisible = false;
  private aspect: PhotoAspect = '16:9';
  private lens: PhotoLens = '1x';
  private capturing = false;
  private keyListenerOn = false;

  private readonly root: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly captureFn: (aspect: PhotoAspect) => Promise<Blob>;
  private readonly saveBlob: (blob: Blob) => void;
  private readonly requestRender: () => void;
  private readonly applyLens: (lens: PhotoLens) => void;
  private readonly onActiveChange?: (active: boolean) => void;

  private readonly framingLayer: HTMLDivElement;
  private readonly frameEl: HTMLDivElement;
  private readonly captureFab: HTMLButtonElement;
  private readonly uiRoot: HTMLDivElement;
  private readonly island: HTMLDivElement;
  private readonly cameraBtn: HTMLButtonElement;
  private readonly controlsRoot: HTMLDivElement;
  private readonly aspectStrip: HTMLDivElement;
  private readonly lensStrip: HTMLDivElement;
  private readonly aspectButtons = new Map<PhotoAspect, HTMLButtonElement>();
  private readonly lensButtons = new Map<PhotoLens, HTMLButtonElement>();

  private boundKey: (e: KeyboardEvent) => void;

  constructor(opts: {
    root: HTMLElement;
    panelRoot: HTMLElement;
    canvas: HTMLCanvasElement;
    captureFn: (aspect: PhotoAspect) => Promise<Blob>;
    saveBlob: (blob: Blob) => void;
    requestRender: () => void;
    setLens?: (lens: PhotoLens) => void;
    onActiveChange?: (active: boolean) => void;
  }) {
    this.root = opts.root;
    this.canvas = opts.canvas;
    this.captureFn = opts.captureFn;
    this.saveBlob = opts.saveBlob;
    this.requestRender = opts.requestRender;
    this.applyLens = opts.setLens ?? (() => {});
    this.onActiveChange = opts.onActiveChange;

    this.framingLayer = document.createElement('div');
    this.framingLayer.className = 'gii-photo-framing';
    this.framingLayer.hidden = true;
    this.framingLayer.innerHTML = `
      <div class="gii-photo-frame">
        <span class="gii-photo-corner gii-photo-corner--tl" aria-hidden="true"></span>
        <span class="gii-photo-corner gii-photo-corner--tr" aria-hidden="true"></span>
        <span class="gii-photo-corner gii-photo-corner--bl" aria-hidden="true"></span>
        <span class="gii-photo-corner gii-photo-corner--br" aria-hidden="true"></span>
      </div>
    `;
    this.frameEl = this.framingLayer.querySelector('.gii-photo-frame') as HTMLDivElement;

    this.captureFab = document.createElement('button');
    this.captureFab.type = 'button';
    this.captureFab.className = 'gii-photo-capture-fab';
    this.captureFab.hidden = true;
    this.captureFab.textContent = 'Capture';
    this.captureFab.addEventListener('click', (e) => {
      e.stopPropagation();
      void this.capture();
    });
    this.framingLayer.appendChild(this.captureFab);
    this.root.appendChild(this.framingLayer);

    this.uiRoot = document.createElement('div');
    this.uiRoot.className = 'gii-photo-ui gii-viewer-chrome gii-viewer-chrome--hidden';
    this.island = document.createElement('div');
    this.island.className = 'gii-photo-island';
    this.island.innerHTML = `
      <button type="button" class="gii-photo-island-btn gii-photo-island-btn--camera" title="Photo mode" aria-label="Photo mode"></button>
    `;
    this.cameraBtn = this.island.querySelector(
      '.gii-photo-island-btn--camera',
    ) as HTMLButtonElement;
    this.cameraBtn.innerHTML = PhotoOverlay.iconCamera();

    this.controlsRoot = document.createElement('div');
    this.controlsRoot.className = 'gii-photo-controls gii-viewer-chrome gii-viewer-chrome--hidden';
    this.controlsRoot.innerHTML = `
      <div class="gii-photo-floating-pill gii-photo-aspect-ui">
        <div class="gii-photo-choice-row gii-photo-aspect-strip" role="group" aria-label="Aspect ratio"></div>
      </div>
      <div class="gii-photo-floating-pill gii-photo-lens-ui">
        <div class="gii-photo-choice-row gii-photo-lens-strip" role="group" aria-label="Focal length"></div>
      </div>
    `;
    this.aspectStrip = this.controlsRoot.querySelector('.gii-photo-aspect-strip') as HTMLDivElement;
    this.lensStrip = this.controlsRoot.querySelector('.gii-photo-lens-strip') as HTMLDivElement;
    this.buildChoiceButtons();

    this.cameraBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.setActive(!this.active);
    });

    this.uiRoot.appendChild(this.island);
    opts.panelRoot.appendChild(this.uiRoot);
    this.root.appendChild(this.controlsRoot);

    this.injectCssOnce();
    this.boundKey = (e) => this.onKeyDown(e);
    this.syncUi();
  }

  isActive(): boolean {
    return this.active;
  }

  setChromeVisible(visible: boolean): void {
    if (this.chromeVisible === visible) return;
    this.chromeVisible = visible;
    this.uiRoot.classList.toggle('gii-viewer-chrome--hidden', !visible);
    this.uiRoot.classList.toggle('gii-viewer-chrome--ready', visible);
    this.syncUi();
  }

  setActive(active: boolean): void {
    if (this.active === active) return;
    this.active = active;
    this.syncKeyListener();
    this.applyLens(active ? this.lens : '1x');
    this.onActiveChange?.(active);
    this.syncUi();
    if (active) this.layoutFrame();
    this.requestRender();
  }

  layoutFrame(): void {
    if (!this.active) return;
    const canvasRect = this.canvas.getBoundingClientRect();
    const rootRect = this.root.getBoundingClientRect();
    const cw = this.canvas.clientWidth;
    const ch = this.canvas.clientHeight;
    if (cw <= 0 || ch <= 0) return;

    const offX = canvasRect.left - rootRect.left;
    const offY = canvasRect.top - rootRect.top;
    const scaleX = canvasRect.width / cw;
    const scaleY = canvasRect.height / ch;
    const frame = computePhotoFrame(cw, ch, this.aspect);

    this.frameEl.style.left = `${offX + frame.x * scaleX}px`;
    this.frameEl.style.top = `${offY + frame.y * scaleY}px`;
    this.frameEl.style.width = `${frame.w * scaleX}px`;
    this.frameEl.style.height = `${frame.h * scaleY}px`;
  }

  dispose(): void {
    this.setActive(false);
    this.syncKeyListener();
    this.framingLayer.remove();
    this.uiRoot.remove();
    this.controlsRoot.remove();
  }

  private buildChoiceButtons(): void {
    for (const aspect of PHOTO_ASPECTS) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gii-photo-choice';
      btn.textContent = photoAspectLabel(aspect);
      btn.title = `Aspect: ${photoAspectLabel(aspect)}`;
      btn.setAttribute('aria-label', `Aspect ${photoAspectLabel(aspect)}`);
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.setAspect(aspect);
      });
      this.aspectButtons.set(aspect, btn);
      this.aspectStrip.appendChild(btn);
    }

    for (const lens of PHOTO_LENSES) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'gii-photo-choice gii-photo-choice--lens';
      btn.textContent = photoLensLabel(lens);
      btn.title = `Lens: ${photoLensLabel(lens)}`;
      btn.setAttribute('aria-label', `Lens ${photoLensLabel(lens)}`);
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        this.setLens(lens);
      });
      this.lensButtons.set(lens, btn);
      this.lensStrip.appendChild(btn);
    }
  }

  private setAspect(aspect: PhotoAspect): void {
    if (this.aspect === aspect) return;
    this.aspect = aspect;
    this.syncUi();
    this.layoutFrame();
    this.requestRender();
  }

  private setLens(lens: PhotoLens): void {
    if (this.lens === lens) return;
    this.lens = lens;
    if (this.active) this.applyLens(lens);
    this.syncUi();
    this.requestRender();
  }

  private async capture(): Promise<void> {
    if (this.capturing || !this.active) return;
    this.capturing = true;
    this.captureFab.disabled = true;
    this.framingLayer.hidden = true;
    try {
      this.requestRender();
      await new Promise<void>((r) => requestAnimationFrame(() => r()));
      const blob = await this.captureFn(this.aspect);
      this.saveBlob(blob);
    } catch (err) {
      console.error('[Photo] capture failed:', err);
    } finally {
      this.captureFab.disabled = false;
      this.capturing = false;
      this.syncUi();
      this.layoutFrame();
      this.requestRender();
    }
  }

  private syncUi(): void {
    this.island.classList.toggle('gii-photo-island--active', this.active);
    this.framingLayer.hidden = !this.active;
    this.captureFab.hidden = !this.active || !this.chromeVisible;
    this.controlsRoot.classList.toggle(
      'gii-viewer-chrome--hidden',
      !this.chromeVisible || !this.active,
    );
    this.controlsRoot.classList.toggle(
      'gii-viewer-chrome--ready',
      this.chromeVisible && this.active,
    );
    this.cameraBtn.classList.toggle('gii-photo-island-btn--active', this.active);
    this.cameraBtn.setAttribute('aria-pressed', String(this.active));
    this.cameraBtn.title = this.active ? 'Exit photo mode' : 'Photo mode';
    this.cameraBtn.setAttribute('aria-label', this.active ? 'Exit photo mode' : 'Photo mode');
    for (const [aspect, btn] of this.aspectButtons) {
      const selected = aspect === this.aspect;
      btn.classList.toggle('gii-photo-choice--active', selected);
      btn.setAttribute('aria-pressed', String(selected));
    }
    for (const [lens, btn] of this.lensButtons) {
      const selected = lens === this.lens;
      btn.classList.toggle('gii-photo-choice--active', selected);
      btn.setAttribute('aria-pressed', String(selected));
    }
  }

  private syncKeyListener(): void {
    if (this.active && !this.keyListenerOn) {
      window.addEventListener('keydown', this.boundKey, { capture: true });
      this.keyListenerOn = true;
    } else if (!this.active && this.keyListenerOn) {
      window.removeEventListener('keydown', this.boundKey, { capture: true } as never);
      this.keyListenerOn = false;
    }
  }

  private onKeyDown(e: KeyboardEvent): void {
    if (!this.active) return;
    if (e.code === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      this.setActive(false);
      return;
    }
    if (e.code === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      void this.capture();
    }
  }

  private injectCssOnce(): void {
    let el = document.getElementById('gii-photo-css') as HTMLStyleElement | null;
    if (!el) {
      el = document.createElement('style');
      el.id = 'gii-photo-css';
      document.head.appendChild(el);
    }
    el.textContent = `
.gii-photo-ui {
  position: absolute; left: 0.65rem; top: 0.65rem; z-index: 58;
  pointer-events: none;
}
.gii-photo-island {
  display: flex; align-items: center;
  background: rgba(8, 16, 30, 0.82);
  border: 1px solid rgba(255,255,255,0.16);
  border-radius: 999px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.06);
  backdrop-filter: blur(14px);
  padding: 0.28rem;
  pointer-events: auto;
  transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
}
.gii-photo-island--active {
  border-color: rgba(255,255,255,0.34);
  background: rgba(10, 18, 30, 0.9);
  box-shadow:
    0 4px 24px rgba(0,0,0,0.35),
    0 0 0 1px rgba(255,255,255,0.08),
    0 0 24px rgba(255,255,255,0.08),
    inset 0 1px 0 rgba(255,255,255,0.08);
}
.gii-photo-island-btn {
  display: grid; place-items: center;
  min-width: 2.35rem; height: 2.35rem;
  border: none; border-radius: 999px;
  background: transparent; color: rgba(255,255,255,0.82);
  cursor: pointer; padding: 0 0.45rem; flex-shrink: 0;
  font: 600 11px/1 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  transition: background 0.15s, color 0.15s, transform 0.12s;
}
.gii-photo-icon {
  display: block;
  width: 1.28rem;
  height: 1.28rem;
  background: currentColor;
  -webkit-mask: url('/artwork/icon_camera01.svg') center / contain no-repeat;
  mask: url('/artwork/icon_camera01.svg') center / contain no-repeat;
}
.gii-photo-island-btn:hover { background: rgba(255,255,255,0.1); color: #fff; }
.gii-photo-island-btn:active { transform: scale(0.94); }
.gii-photo-island-btn--active {
  background: rgba(255, 255, 255, 0.16);
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.32);
}
.gii-photo-controls {
  position: absolute; inset: 0; z-index: 57;
  pointer-events: none;
}
.gii-photo-floating-pill {
  position: absolute; left: 50%;
  transform: translateX(-50%);
  display: flex; align-items: center; justify-content: center;
  pointer-events: auto;
  max-width: calc(100vw - 7.25rem);
  padding: 0.24rem;
  border: 1px solid rgba(255,255,255,0.16);
  border-radius: 999px;
  background: rgba(8, 16, 30, 0.78);
  box-shadow: 0 8px 32px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.06);
  backdrop-filter: blur(16px);
}
.gii-photo-aspect-ui {
  top: calc(0.65rem + env(safe-area-inset-top, 0px) + 0.4rem);
}
.gii-photo-lens-ui {
  bottom: calc(4.75rem + env(safe-area-inset-bottom, 0));
}
.pc-canvas-viewport--photo-tour-active .gii-photo-lens-ui {
  bottom: calc(6.75rem + env(safe-area-inset-bottom, 0px));
}
.gii-photo-choice-row {
  display: flex; gap: 0.16rem;
  max-width: 100%;
  overflow-x: auto;
  scrollbar-width: none;
}
.gii-photo-choice-row::-webkit-scrollbar {
  display: none;
}
.gii-photo-choice {
  appearance: none;
  min-width: 2.55rem; height: 1.62rem;
  border: 1px solid rgba(255,255,255,0.11);
  border-radius: 999px;
  background: rgba(255,255,255,0.045);
  color: rgba(255,255,255,0.72);
  cursor: pointer;
  padding: 0 0.44rem;
  font: 600 10.5px/1 ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  transition: background 0.15s, color 0.15s, border-color 0.15s;
}
.gii-photo-choice:hover { background: rgba(255,255,255,0.1); color: #fff; }
.gii-photo-choice--active {
  background: rgba(255,255,255,0.16);
  border-color: rgba(255,255,255,0.38);
  color: #fff;
}
.gii-photo-choice--lens { min-width: 3rem; }
.gii-photo-framing {
  position: absolute; inset: 0; z-index: 45;
  pointer-events: none; overflow: hidden;
}
.gii-photo-frame {
  position: absolute;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.52);
  border: 1px solid rgba(255, 255, 255, 0.14);
  pointer-events: none;
}
.gii-photo-corner {
  position: absolute; width: 22px; height: 22px;
  border-color: rgba(255, 255, 255, 0.92);
  border-style: solid; pointer-events: none;
}
.gii-photo-corner--tl { top: -1px; left: -1px; border-width: 2px 0 0 2px; }
.gii-photo-corner--tr { top: -1px; right: -1px; border-width: 2px 2px 0 0; }
.gii-photo-corner--bl { bottom: -1px; left: -1px; border-width: 0 0 2px 2px; }
.gii-photo-corner--br { bottom: -1px; right: -1px; border-width: 0 2px 2px 0; }
.gii-photo-capture-fab {
  position: absolute; left: 50%; bottom: 1.35rem;
  transform: translateX(-50%);
  pointer-events: auto;
  background: rgba(255, 255, 255, 0.94);
  color: #0a1424; border: none;
  border-radius: 999px;
  padding: 0.55rem 1.35rem;
  font: 600 13px/1 system-ui, -apple-system, Segoe UI, Roboto, Arial;
  cursor: pointer;
  box-shadow: 0 4px 24px rgba(0,0,0,0.4);
  transition: transform 0.12s, opacity 0.12s;
}
.pc-canvas-viewport--photo-tour-active .gii-photo-capture-fab {
  bottom: calc(3.65rem + env(safe-area-inset-bottom, 0px));
}
.gii-photo-capture-fab:hover { transform: translateX(-50%) scale(1.03); }
.gii-photo-capture-fab:disabled { opacity: 0.55; cursor: wait; }
@media (max-width: 700px) {
  .gii-photo-ui {
    left: calc(0.65rem + env(safe-area-inset-left, 0));
    top: calc(0.65rem + env(safe-area-inset-top, 0));
  }
  .gii-photo-floating-pill {
    max-width: calc(100vw - 6.8rem - env(safe-area-inset-left, 0) - env(safe-area-inset-right, 0));
  }
  .gii-photo-choice { min-width: 2.5rem; }
  .gii-photo-choice--lens { min-width: 2.85rem; }
}
`;
  }

  private static iconCamera(): string {
    return `<span class="gii-photo-icon" aria-hidden="true"></span>`;
  }
}
